import { ArrowRight } from 'lucide-react';
import * as React from 'react';

import { DataTable, StatusChip, type DataTableState, type IDataColumn, type IDataGrouping } from '@/design';
import { isCustomGroup, type IMailTemplateSummary } from '../../api/mail-template-contracts';
import { LoadError } from '../../components/admin-page';
import { formatEt, surveyLabel } from './template-fields';
import type { TemplateStatus } from './template-nav';
import { GROUPS, notInUse } from './template-picker';
import { TagChips, USAGES } from './template-tags';

/**
 * Setup → Email templates with no template open: every template the caller may edit, grouped as
 * the picker groups them, each with what it is for — a custom one with where it can be used — its
 * tags, the text its mails go with now and the surveys with text of their own. A row opens its
 * template. Above it the filters, a tag and a status: Active, Archived or All.
 */
export function TemplateOverview({
  templates,
  error,
  tagged = false,
  status,
  filters,
  onOpen
}: {
  /** Null until the list is read. */
  templates: Nullable<IMailTemplateSummary[]>;
  error: Nullable<string>;
  /** A tag narrows the list. */
  tagged?: boolean;
  status: TemplateStatus;
  /** The filter row above the table. */
  filters: React.ReactNode;
  onOpen: (code: string) => void;
}) {
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(new Set());
  const grouping = React.useMemo<IDataGrouping<IMailTemplateSummary>>(
    () => ({
      levels: [
        {
          id: 'group',
          label: 'Group',
          value: (template) => template.group ?? 'survey',
          compare: (left, right) => groupOrder(left) - groupOrder(right),
          name: (group) => GROUPS.find((entry) => entry.group === group)?.heading ?? group
        }
      ],
      collapsed,
      onToggle: (key) =>
        setCollapsed((current) => {
          const next = new Set(current);
          if (!next.delete(key)) next.add(key);
          return next;
        })
    }),
    [collapsed]
  );

  return (
    <DataTable
      label="Email templates"
      columns={COLUMNS}
      rows={templates && templates.length > 0 ? templates : null}
      rowKey={(template) => template.code}
      rowProps={(template) => ({ 'data-template-row': template.code }) as React.HTMLAttributes<HTMLTableRowElement>}
      onOpen={(template) => onOpen(template.code)}
      grouping={grouping}
      toolbar={filters}
      state={overviewState(templates === null, error, tagged, status)}
    />
  );
}

function groupOrder(group: string): number {
  const at = GROUPS.findIndex((entry) => entry.group === group);
  return at < 0 ? GROUPS.length : at;
}

function overviewState(
  unread: boolean,
  error: Nullable<string>,
  tagged: boolean,
  status: TemplateStatus
): DataTableState {
  if (unread && error) return { kind: 'error', content: <LoadError message={error} /> };
  if (unread) return { kind: 'loading' };
  const archived = status === 'archived';
  const content = tagged
    ? archived ? 'No archived template has this tag.' : 'No template has this tag.'
    : archived ? 'No archived templates.' : 'No email templates to edit.';
  return { kind: 'empty', content };
}

/** The text a template's mails go with now: the default written in code, a version, or — for a
 *  staff template, whose version 0 is empty — none yet. */
function currentText(template: IMailTemplateSummary): string {
  const base = template.variants.find((variant) => variant.survey === null);
  const version = base?.version ?? 0;
  if (version > 0) return base?.currentAt ? `Version ${version} · ${dayOf(base.currentAt)}` : `Version ${version}`;
  return isCustomGroup(template.group) ? 'Not published' : 'Default';
}

/** "Nov 1, 2026". */
function dayOf(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const COLUMNS: IDataColumn<IMailTemplateSummary>[] = [
  {
    id: 'name',
    header: 'Email',
    className: 'max-w-0 w-full',
    cell: (template) => (
      <span className="grid min-w-0">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium">{template.name}</span>
          <TagChips tags={template.tags} max={3} className="shrink-0 flex-nowrap whitespace-nowrap" />
        </span>
        {template.purpose && (
          <span className="truncate text-xs text-muted-foreground" title={template.purpose}>
            {template.purpose}
          </span>
        )}
      </span>
    )
  },
  {
    id: 'usedIn',
    header: 'Used in',
    cell: (template) =>
      template.usages.length > 0 ? (
        <span className="whitespace-nowrap" data-usages="">
          {USAGES.filter((usage) => template.usages.includes(usage.value))
            .map((usage) => usage.short)
            .join(' · ')}
        </span>
      ) : (
        <span className="whitespace-nowrap text-muted-foreground">Portal events</span>
      )
  },
  {
    id: 'state',
    header: 'State',
    cell: (template) => {
      const scheduled = template.variants.find((variant) => variant.scheduledAt)?.scheduledAt ?? null;
      return (
        <span className="flex flex-wrap items-center gap-1.5 whitespace-nowrap" data-template-state="">
          <span className={currentText(template) === 'Not published' ? 'text-muted-foreground' : undefined}>
            {currentText(template)}
          </span>
          {template.archivedAt && <StatusChip tone="neutral">Archived</StatusChip>}
          {notInUse(template) && <StatusChip tone="attention">Not in use</StatusChip>}
          {scheduled && <StatusChip tone="info">Scheduled {formatEt(scheduled)}</StatusChip>}
        </span>
      );
    }
  },
  {
    id: 'surveys',
    header: 'Own text',
    cell: (template) => {
      if (!template.surveyScoped) return null;
      const own = template.variants.filter((variant) => variant.survey !== null && !variant.usesBase);
      return own.length > 0 ? (
        <span className="whitespace-nowrap">{own.map((variant) => surveyLabel(variant.survey)).join(', ')}</span>
      ) : (
        <span className="text-muted-foreground">None</span>
      );
    }
  },
  {
    id: 'open',
    header: '',
    label: 'Open',
    headClassName: 'w-10',
    cell: () => <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
  }
];
